import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/SimpleKeycloakProvider';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  Divider,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';

export const LoginPage: React.FC = () => {
  const { authenticated, login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (authenticated) {
      navigate('/');
    }
  }, [authenticated, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.dark} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative background circles */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: alpha(theme.palette.primary.light, 0.1),
          top: -250,
          right: -250,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: alpha(theme.palette.secondary.light, 0.1),
          bottom: -200,
          left: -200,
        }}
      />

      <Container maxWidth="md">
        <Grid container spacing={4} alignItems="center">
          {/* Left side - Branding */}
          <Grid item xs={12} md={6}>
            <Box sx={{ color: 'white', pr: { md: 4 } }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    width: 64,
                    height: 64,
                  }}
                >
                  <BankIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    SUMA
                  </Typography>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    Financiera
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Credit Risk ML Platform
              </Typography>

              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Advanced AI-Powered Credit Risk Assessment System
              </Typography>

              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AnalyticsIcon />
                  <Typography>Machine Learning Models</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <SpeedIcon />
                  <Typography>Real-time Risk Analysis</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <SecurityIcon />
                  <Typography>Enterprise Security</Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {/* Right side - Login Card */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={24}
              sx={{
                p: 5,
                borderRadius: 3,
                background: alpha(theme.palette.background.paper, 0.98),
                backdropFilter: 'blur(20px)',
              }}
            >
              <Box textAlign="center" mb={4}>
                <Avatar
                  sx={{
                    m: 'auto',
                    mb: 2,
                    bgcolor: theme.palette.primary.main,
                    width: 56,
                    height: 56,
                  }}
                >
                  <LockIcon />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign in to access the platform
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={login}
                startIcon={<SecurityIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                  boxShadow: `0 3px 5px 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                  },
                }}
              >
                Sign in with Keycloak
              </Button>

              <Divider sx={{ my: 3 }}>
                <Chip label="SECURE AUTHENTICATION" size="small" />
              </Divider>

              <Grid container spacing={2} textAlign="center">
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    3
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ML Models
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    99.9%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uptime
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    24/7
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Support
                  </Typography>
                </Grid>
              </Grid>

              <Box mt={3} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  By signing in, you agree to our Terms of Service
                </Typography>
              </Box>
            </Paper>

            <Box mt={3} textAlign="center">
              <Typography variant="caption" sx={{ color: 'white', opacity: 0.7 }}>
                © 2024 SUMA Financiera - All Rights Reserved
              </Typography>
              <Typography variant="caption" sx={{ color: 'white', opacity: 0.5, display: 'block' }}>
                Powered by Moveo Ventures
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};